import api from '../../../../services/api/';
const BASE_URL = '/stripe_connect';

/**
 * Bank Connect Service
 * Handles all API calls for bank account setup and online payment management
 * Powered by Stripe Connect (internal implementation, not exposed to users)
 * 
 * Flow:
 * 1. Business Type Selection
 * 2. Business Details
 * 3. Person/Owner Details
 * 4. Bank Account Details
 * 5. Services Agreement
 */
class BankConnectService {

    // ============================================
    // Main Account Management
    // ============================================

    /**
     * Get bank connect account information
     * Returns the organization's online payment account setup progress and data
     */
    getAccount() {
        return api.get(`${BASE_URL}/account`);
    }

    /**
     * Get full bank account details
     * Returns complete account info including capabilities, payouts, connected banks, etc.
     */
    getAccountDetails() {
        return api.get(`${BASE_URL}/account/details`);
    }

    /**
     * Save progress (finish later functionality)
     * Allows partial save of account setup
     */
    saveProgress(data) {
        return api.post(`${BASE_URL}/account/save_progress`, data);
    }

    /**
     * Disable online payment account
     */
    disableAccount() {
        return api.post(`${BASE_URL}/account/disable`);
    }

    // ============================================
    // Utilities
    // ============================================

    /**
     * Get list of Merchant Category Codes (MCC)
     */
    getMCCList() {
        return api.get(`${BASE_URL}/mcc_list`);
    }

    // ============================================
    // Person Details (View Only)
    // ============================================

    /**
     * Get person details by public_id
     * @param {string} public_id - Person public ID
     */
    getPerson(public_id) {
        return api.get(`${BASE_URL}/person/${public_id}`);
    }

    // ============================================
    // Bank Account Details
    // ============================================

    /**
     * Get external bank account details by public_id
     * @param {string} public_id - Account public ID
     */
    getExternalAccount(public_id) {
        return api.get(`${BASE_URL}/external_account/${public_id}`);
    }

    /**
     * Get all external (payout) bank accounts from the local store.
     * @param {boolean} refresh - when true, re-syncs from Stripe first
     */
    getAllExternalAccounts(refresh = false) {
        return api.get(`${BASE_URL}/external_accounts`, { params: refresh ? { refresh: 1 } : {} });
    }

    /**
     * Add additional external bank account manually
     * (Use Stripe hosted onboarding for initial setup)
     */
    updateExternalAccount(data) {
        return api.post(`${BASE_URL}/external_account`, data);
    }

    /**
     * Set a payout bank as the default (the one that receives payouts).
     * @param {string} public_id - the payout bank's public_id
     */
    selectDefaultBank(public_id) {
        return api.post(`${BASE_URL}/external_account/set_default`, { public_id });
    }

    /**
     * Remove (detach) a payout bank. Promotes another bank to default first if needed.
     * @param {string} public_id - the payout bank's public_id
     */
    deleteExternalAccount(public_id) {
        return api.delete(`${BASE_URL}/external_account/${public_id}`);
    }

    // ============================================
    // Hosted Onboarding (Stripe Account Link)
    // ============================================

    /**
     * Create an Account Session for embedded Connect components.
     * Returns { client_secret, publishable_key, expires_at, status }. Used to render
     * onboarding / management INSIDE the app (no redirect to Stripe).
     */
    createAccountSession() {
        return api.post(`${BASE_URL}/account/session`);
    }

    /**
     * Create account link for Stripe hosted onboarding (fallback / redirect flow)
     * @param {Object} params - { return_url?, refresh_url? }
     */
    createAccountLink(params = {}) {
        return api.post(`${BASE_URL}/account/create_link`, params);
    }

    /**
     * Create account link specifically for adding/updating bank account
     * Use this when user wants to update bank account information
     * @param {Object} params - { return_url?, refresh_url? }
     */
    createBankAccountLink(params = {}) {
        return api.post(`${BASE_URL}/account/create_bank_link`, params);
    }

    /**
     * Handle return from Stripe hosted onboarding
     * Updates local account status based on Stripe account state
     */
    handleOnboardingReturn() {
        return api.get(`${BASE_URL}/account/onboarding_return`);
    }

    // ============================================
    // Legacy Method Aliases (for backward compatibility)
    // ============================================
    
    getPaymentStripeAccount() {
        return this.getAccount();
    }

    getStripeAccount() {
        return this.getAccountDetails();
    }

    setDisableAccount() {
        return this.disableAccount();
    }

    getMCCData() {
        return this.getMCCList();
    }

    postSaveLaterStripeAccount(data) {
        return this.saveProgress(data);
    }

    getExternalAccountAll() {
        return this.getAllExternalAccounts();
    }

    postHandleBankDetailsUpdate(data) {
        return this.updateExternalAccount(data);
    }

    postHandleBankSelectUpdate(data) {
        return this.selectDefaultBank(data);
    }
}

export default new BankConnectService();